import * as Sentry from "@sentry/react";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { diagnosticsSessionId } from "src/lib/diagnosticsSession";
import { isPartnerDiagnosticsEnabled } from "src/lib/partnerDiagnostics";
import { tracker } from "src/state/state";

const MARKER_KEY = "learnDiagnosticsMarker";

type Mark = { stage: string; ms: number; data?: string };
type Marker = { sessionId: string; lastMarkAt: number; marks: Mark[] };

let visitStart = 0;
let visitActive = false;
let marks: Mark[] = [];

const readMarker = (): Marker | null => {
  try {
    const raw = localStorage.getItem(MARKER_KEY);
    return raw ? (JSON.parse(raw) as Marker) : null;
  } catch {
    return null;
  }
};

const writeMarker = (): void => {
  try {
    localStorage.setItem(
      MARKER_KEY,
      JSON.stringify({
        sessionId: diagnosticsSessionId,
        lastMarkAt: Date.now(),
        marks
      } satisfies Marker)
    );
  } catch {
    // storage unavailable
  }
};

const clearMarker = (): void => {
  try {
    localStorage.removeItem(MARKER_KEY);
  } catch {
    // storage unavailable
  }
};

export const getMarks = (): Mark[] => marks;

export const mark = (
  stage: string,
  data?: Record<string, string | number | boolean | undefined>
): void => {
  if (!isPartnerDiagnosticsEnabled()) {
    return;
  }
  const entry: Mark = {
    stage,
    ms: Math.round(performance.now() - visitStart),
    data: data ? JSON.stringify(data) : undefined
  };
  marks = [...marks, entry].slice(-50);
  addSentryBreadcrumb("learn-diagnostics", stage, "info", data);
  if (visitActive) {
    writeMarker();
  }
};

export const endLearnVisit = (): void => {
  visitActive = false;
  window.removeEventListener("pagehide", clearMarker);
  clearMarker();
};

export const startLearnVisit = (): void => {
  if (!isPartnerDiagnosticsEnabled()) {
    return;
  }
  visitStart = performance.now();
  marks = [];
  visitActive = true;
  window.addEventListener("pagehide", clearMarker);
  writeMarker();
};

export const reportUncleanLearnExit = (): void => {
  const marker = readMarker();
  if (!marker) {
    return;
  }
  clearMarker();
  const navigationType = (
    performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined
  )?.type;
  const msSinceLastMark = Date.now() - marker.lastMarkAt;
  Sentry.captureMessage("Learn unclean exit", {
    extra: { ...marker, msSinceLastMark, navigationType }
  });
  tracker.track("Learn Diagnostics Unclean Exit", {
    data: {
      sessionId: diagnosticsSessionId,
      previousSessionId: marker.sessionId,
      marks: JSON.stringify(marker.marks),
      msSinceLastMark,
      navigationType
    }
  });
};
